package fr.frux.core.maps

import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonObjectBuilder
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.maplibre.spatialk.geojson.Feature
import org.maplibre.spatialk.geojson.FeatureCollection
import org.maplibre.spatialk.geojson.Point

/**
 * Pyramide de regroupements calculee par le serveur (#622, ADR 0039) : pour chaque zoom entier de 0
 * a [maxZoom], les points que la carte aurait formes elle-meme en regroupant tous les arbres. Sans
 * vocabulaire metier, comme [MapMarker] : l'appelant a deja traduit especes et etats en badges et
 * en marqueurs.
 */
data class MapPyramid(
    val maxZoom: Int,
    val points: List<MapPyramidPoint>,
)

/** Un point de la pyramide, valable au seul zoom entier [zoom]. */
sealed interface MapPyramidPoint {
    val position: Location
    val zoom: Int

    data class Cluster(
        override val position: Location,
        override val zoom: Int,
        val count: Int,
        /** Premier zoom ou l'amas se separe, ou null quand le serveur ne l'a pas fourni. */
        val expansionZoom: Int?,
        /** Badges les plus representes, deja classes, [MAX_CLUSTER_SATELLITES] au plus. */
        val satellites: List<MarkerBadge>,
    ) : MapPyramidPoint

    /** Un arbre qu'aucun amas ne contient a ce zoom : il se dessine comme dans le lot detaille. */
    data class Single(
        override val zoom: Int,
        val marker: MapMarker,
    ) : MapPyramidPoint {
        override val position: Location get() = marker.position
    }
}

internal const val PYRAMID_ZOOM_PROPERTY = "z"
internal const val PYRAMID_KIND_PROPERTY = "kind"
internal const val PYRAMID_KIND_CLUSTER = "cluster"
internal const val PYRAMID_KIND_SINGLE = "single"
internal const val PYRAMID_COUNT_PROPERTY = "n"
internal const val PYRAMID_EXPANSION_PROPERTY = "e"
internal const val PYRAMID_SATELLITE_COUNT_PROPERTY = "sat_count"

internal fun pyramidSatelliteProperty(rank: Int): String = "sat_$rank"

private const val PYRAMID_FALLBACK_ZOOM_STEP = 2f

/**
 * Zoom vise au tap sur un amas de la pyramide. Le zoom d'eclatement vient du serveur, nul besoin
 * d'interroger la source. Sans lui, le meme pas fixe que pour un amas du lot detaille. Jamais de
 * retour en arriere, jamais au-dela du zoom maximal de la carte.
 */
internal fun pyramidTapZoom(
    currentZoom: Float,
    expansionZoom: Int?,
): Float {
    val target = expansionZoom?.toFloat() ?: (currentZoom + PYRAMID_FALLBACK_ZOOM_STEP)
    return maxOf(target, currentZoom).coerceAtMost(MAX_MAP_ZOOM)
}

/**
 * Points de la pyramide en GeoJSON. Les satellites portent directement leur nom d'image, deja
 * classe : la couche n'a ni agregat ni expression de rang a evaluer.
 */
internal fun MapPyramid.toFeatureCollection(): FeatureCollection<Point, JsonObject?> =
    FeatureCollection(
        features =
            points.map { point ->
                Feature(
                    geometry = Point(point.position.toPosition()),
                    properties =
                        buildJsonObject {
                            put(PYRAMID_ZOOM_PROPERTY, point.zoom)
                            when (point) {
                                is MapPyramidPoint.Cluster -> putCluster(point)
                                is MapPyramidPoint.Single -> putSingle(point)
                            }
                        },
                )
            },
    )

private fun JsonObjectBuilder.putCluster(cluster: MapPyramidPoint.Cluster) {
    put(PYRAMID_KIND_PROPERTY, PYRAMID_KIND_CLUSTER)
    put(PYRAMID_COUNT_PROPERTY, cluster.count)
    cluster.expansionZoom?.let { put(PYRAMID_EXPANSION_PROPERTY, it) }
    val satellites = cluster.satellites.take(MAX_CLUSTER_SATELLITES)
    put(PYRAMID_SATELLITE_COUNT_PROPERTY, satellites.size)
    satellites.forEachIndexed { rank, badge -> put(pyramidSatelliteProperty(rank), badgeKey(badge)) }
}

// Memes proprietes que le lot detaille (cf. toFeatureCollection des marqueurs) : la couche des
// isoles choisit son image par la meme cle de style.
private fun JsonObjectBuilder.putSingle(single: MapPyramidPoint.Single) {
    put(PYRAMID_KIND_PROPERTY, PYRAMID_KIND_SINGLE)
    put(MARKER_ID_PROPERTY, single.marker.id.value)
    put(MARKER_STYLE_PROPERTY, markerStyleKey(single.marker))
    put(MARKER_ALPHA_PROPERTY, single.marker.alpha)
}
